import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { fmtNZD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NumberInput from "@/components/NumberInput";

export default function TasksTab() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const isMgmt = user?.role === "Admin" || user?.role === "Manager";
  const [stages, setStages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [newName, setNewName] = useState("");
  const [newStageName, setNewStageName] = useState("");
  const [addingStage, setAddingStage] = useState(false);
  const [stageMsg, setStageMsg] = useState("");
  const [stageErr, setStageErr] = useState("");
  const [stageLabour, setStageLabour] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [picked, setPicked] = useState("");
  const [applying, setApplying] = useState(false);
  const [tplMsg, setTplMsg] = useState("");
  const [tplErr, setTplErr] = useState("");
  const [confirm, setConfirm] = useState(null); // {existingName, templateName}
  const [dedupeMsg, setDedupeMsg] = useState("");

  async function load() {
    const [s, t, b, sl] = await Promise.all([
      api.get(`/projects/${project.id}/stages`),
      api.get(`/projects/${project.id}/tasks`),
      api.get("/build-templates").catch(()=>({data:[]})),
      api.get(`/projects/${project.id}/stage-labour`).catch(()=>({data:{stages:[]}})),
    ]);
    setStages(s.data); setTasks(t.data); setTemplates(b.data); setStageLabour(sl.data?.stages||[]);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  async function addTask(e) {
    e.preventDefault(); if (!newName.trim()) return;
    await api.post("/tasks", { project_id: project.id, name: newName });
    setNewName(""); load();
  }
  async function toggleDone(t) { await api.patch(`/tasks/${t.id}`, { ...t, done: !t.done }); load(); }
  async function del(t) {
    if (!window.confirm(`Delete task "${t.name}"?`)) return;
    await api.delete(`/tasks/${t.id}`); load();
  }

  async function handleApplyClick() {
    if (!picked || applying) return;
    setTplErr(""); setTplMsg("");
    try {
      const { data: state } = await api.get(`/projects/${project.id}/build-template-state`);
      if (state.has_stages) {
        setConfirm({ existingName: project.build_template || "a previous template", templateName: picked });
        return;
      }
      await doApply("add_missing");
    } catch (e) {
      setTplErr(e?.response?.data?.detail || "Could not check template state");
    }
  }

  async function doApply(mode) {
    setApplying(true); setTplErr(""); setTplMsg(""); setConfirm(null);
    try {
      const { data } = await api.post(`/projects/${project.id}/apply-build-template`,
                                      { template_name: picked, mode });
      const parts = [`Template applied — ${data.template_name}`];
      if (data.added) parts.push(`${data.added} added`);
      if (data.already_present) parts.push(`${data.already_present} already present`);
      if (data.removed) parts.push(`${data.removed} removed`);
      setTplMsg(parts.join(" · "));
      setPicked("");
      await load();
    } catch (e) {
      setTplErr(e?.response?.data?.detail || "Apply failed");
    } finally { setApplying(false); }
  }

  async function dedupe() {
    if (!window.confirm("Merge duplicate stages? The one with the most logged hours (or budget) is kept and its tasks reassigned.")) return;
    setDedupeMsg("");
    try {
      const { data } = await api.post(`/projects/${project.id}/stages/dedupe`);
      setDedupeMsg(data.merged
        ? `Merged ${data.merged} duplicate stage${data.merged===1?"":"s"} · ${data.reassigned_tasks} task${data.reassigned_tasks===1?"":"s"} reassigned`
        : "No duplicates found");
      await load();
    } catch (e) {
      setDedupeMsg(e?.response?.data?.detail || "Dedupe failed");
    }
  }

  async function addStage(e) {
    if (e) e.preventDefault();
    const name = newStageName.trim();
    if (!name) return;
    setStageErr(""); setStageMsg("");
    try {
      await api.post("/stages", { project_id: project.id, name });
      setNewStageName(""); setAddingStage(false);
      setStageMsg(`Added stage "${name}" ✓`);
      setTimeout(() => setStageMsg(""), 2500);
      await load();
    } catch (ex) {
      setStageErr(ex?.response?.data?.detail || "Could not add stage");
    }
  }

  async function deleteStage(s) {
    if (!window.confirm(`Delete stage "${s.name}"? Tasks in it will become Other tasks.`)) return;
    try {
      await api.delete(`/stages/${s.id}`);
      await load();
    } catch (ex) {
      alert(ex?.response?.data?.detail || "Could not delete stage");
    }
  }

  const unstaged = tasks.filter(t => !t.stage_id);
  const byStage = (sid) => tasks.filter(t => t.stage_id === sid);
  const labourFor = (sid) => stageLabour.find(x => x.id === sid);

  // Count duplicates to show dedupe button only when relevant
  const duplicateCount = (() => {
    const counts = {};
    stages.forEach(s => { counts[s.name] = (counts[s.name] || 0) + 1; });
    return Object.values(counts).filter(n => n > 1).reduce((a,b)=> a+(b-1), 0);
  })();

  return (
    <div className="space-y-4" data-testid="tasks-tab">
      <div className="card">
        <div className="flex gap-2 items-end flex-wrap">
          <label className="text-sm">Load build template
            <select className="input" value={picked} onChange={e=>setPicked(e.target.value)} data-testid="tpl-picker">
              <option value="">—</option>
              {templates.map(t=>(<option key={t.id} value={t.name}>{t.name}</option>))}
            </select></label>
          <button className="btn-silver" onClick={handleApplyClick} data-testid="btn-apply-template"
                  disabled={!picked || applying}>
            {applying ? "Applying…" : "Apply template"}
          </button>
          {duplicateCount > 0 && (
            <button className="btn-ghost text-sm" onClick={dedupe} data-testid="btn-dedupe-stages"
                    title="Merge stages that share the same name">
              Remove duplicate stages ({duplicateCount})
            </button>
          )}
          {isMgmt && !addingStage && (
            <button className="btn-ghost text-sm" onClick={() => { setAddingStage(true); setStageErr(""); }}
                    data-testid="btn-add-stage">+ Add stage</button>
          )}
          {isMgmt && addingStage && (
            <form onSubmit={addStage} className="flex gap-2 items-center" data-testid="add-stage-form">
              <input autoFocus className="input" placeholder="New stage name" value={newStageName}
                     onChange={e => setNewStageName(e.target.value)} data-testid="input-new-stage"/>
              <button className="btn-silver" type="submit" data-testid="btn-save-new-stage">Add</button>
              <button type="button" className="btn-ghost text-sm"
                      onClick={() => { setAddingStage(false); setNewStageName(""); setStageErr(""); }}>Cancel</button>
            </form>
          )}
        </div>
        {stageMsg && <div className="text-green-400 text-sm mt-2" data-testid="stage-msg">{stageMsg}</div>}
        {stageErr && <div className="text-red-500 text-sm mt-2" data-testid="stage-err">{stageErr}</div>}
        {tplMsg && <div className="text-green-400 text-sm mt-2" data-testid="tpl-success">{tplMsg}</div>}
        {tplErr && <div className="text-red-500 text-sm mt-2" data-testid="tpl-error">{tplErr}</div>}
        {dedupeMsg && <div className="text-silver text-sm mt-2" data-testid="dedupe-msg">{dedupeMsg}</div>}
      </div>

      {confirm && (
        <div className="fixed inset-0 bg-black/80 z-40 flex items-center justify-center p-4" data-testid="tpl-confirm">
          <div className="card max-w-md w-full">
            <h3 className="text-silver mb-2">Template already applied</h3>
            <p className="text-sm opacity-80 mb-4">
              This project already has stages from <b>{confirm.existingName}</b>. Would you like to replace them, add only missing ones, or cancel?
            </p>
            <div className="flex flex-wrap gap-2">
              <button className="btn-silver" data-testid="tpl-confirm-add" onClick={() => doApply("add_missing")}>Add only missing</button>
              <button className="btn-ghost" data-testid="tpl-confirm-replace" onClick={() => doApply("replace")}>Replace</button>
              <button className="btn-ghost" data-testid="tpl-confirm-cancel" onClick={() => setConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={addTask} className="flex gap-2">
        <input data-testid="task-input" className="input" placeholder="Add task…" value={newName} onChange={e=>setNewName(e.target.value)}/>
        <button className="btn-silver" data-testid="btn-add-task">Add</button>
      </form>
      {stages.length > 0 && stages.map(s => {
        const lf = labourFor(s.id);
        const fromEstimate = lf?.from_estimate_hours || 0;
        return (
          <div key={s.id} className="card" data-testid={`stage-${s.id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <input className="input text-silver max-w-xs" value={s.name}
                       onChange={e => setStages(ss => ss.map(x => x.id === s.id ? { ...x, name: e.target.value } : x))}
                       onBlur={e => api.patch(`/stages/${s.id}`, { name: e.target.value }).then(load)}
                       data-testid={`stage-name-${s.id}`}/>
                <button className="text-xs text-red-400"
                        onClick={() => deleteStage(s)}
                        data-testid={`btn-stage-del-${s.id}`}>Delete</button>
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <label className="flex items-center gap-1">
                  <span className="opacity-70">Labour budget (h)</span>
                  <NumberInput className="input w-24 text-right"
                         value={s.labour_hours_budget ?? 0}
                         onChange={e => setStages(ss => ss.map(x => x.id === s.id ? { ...x, labour_hours_budget: parseFloat(e.target.value || 0) } : x))}
                         onBlur={e => api.patch(`/stages/${s.id}`, { labour_hours_budget: parseFloat(e.target.value || 0) }).then(load)}
                         data-testid={`stage-budget-${s.id}`}/>
                </label>
                {lf?.source === "estimate" && (
                  <span className="text-[10px] bg-silver text-black px-1 rounded" data-testid={`stage-src-${s.id}-estimate`}>from estimate</span>
                )}
                {lf?.source === "manual" && (
                  <>
                    <span className="text-[10px] border border-amber-400 text-amber-300 px-1 rounded" data-testid={`stage-src-${s.id}-manual`}>manual</span>
                    {lf?.from_estimate_hours > 0 && (
                      <button className="hub-link text-[11px]" data-testid={`btn-reset-estimate-${s.id}`}
                              onClick={async () => { await api.post(`/stages/${s.id}/reset-to-estimate`); load(); }}>
                        Reset to estimate ({lf.from_estimate_hours}h)
                      </button>
                    )}
                  </>
                )}
                {lf?.source === "template" && (
                  <span className="text-[10px] border border-neutral-700 text-neutral-400 px-1 rounded" data-testid={`stage-src-${s.id}-template`}>template</span>
                )}
                {lf?.source === "estimate" && (
                  <span className="opacity-60" data-testid={`stage-from-est-${s.id}`}>estimate hours: {lf.from_estimate_hours}h</span>
                )}
                {lf?.source !== "estimate" && lf?.from_estimate_hours > 0 && (
                  <span className="opacity-50 text-[11px]" data-testid={`stage-est-hint-${s.id}`}>estimate suggests {lf.from_estimate_hours}h</span>
                )}
                {lf && <span>used {lf.used}h · <b className={lf.remaining < 0 ? "text-red-400" : "text-silver"}>remaining {lf.remaining}h</b></span>}
              </div>
            </div>
            <TaskList items={byStage(s.id)} onToggle={toggleDone} onDel={del}/>
          </div>
        );
      })}
      <div className="card">
        <h3 className="text-silver mb-2">{stages.length ? "Other tasks" : "Tasks"}</h3>
        <TaskList items={unstaged} onToggle={toggleDone} onDel={del}/>
      </div>
    </div>
  );
}

function TaskList({ items, onToggle, onDel }) {
  if (!items.length) return <div className="opacity-60 text-sm" data-testid="stage-tasks-empty">
    No tasks (optional) — add a task above if you want a checklist under this stage. The stage's labour budget and remaining still work without tasks.
  </div>;
  return (
    <ul className="space-y-1">
      {items.map(t => (
        <li key={t.id} data-testid={`task-${t.id}`} className="flex items-center justify-between gap-2 py-1 border-b border-neutral-900">
          <label className="flex items-center gap-2 flex-1 cursor-pointer">
            <input type="checkbox" checked={!!t.done} onChange={() => onToggle(t)} />
            <span className={t.done ? "line-through opacity-50" : ""}>{t.name}</span>
          </label>
          {onDel && <button onClick={() => onDel(t)} className="text-xs text-red-400 hover:text-red-300">Delete</button>}
        </li>
      ))}
    </ul>
  );
}
